/**
 * QR Code Scanner Component
 *
 * Uses device camera to scan QR codes for room joining.
 */

import { useState, useCallback } from 'react'
import { Scanner, IDetectedBarcode } from '@yudiel/react-qr-scanner'

interface QRScannerProps {
  onScan: (data: string) => void
  onClose: () => void
  onError?: (error: Error) => void
}

export function QRScanner({ onScan, onClose, onError }: QRScannerProps) {
  const [error, setError] = useState<string | null>(null)
  const [hasPermission, setHasPermission] = useState(true)

  const handleScan = useCallback(
    (detectedCodes: IDetectedBarcode[]) => {
      const firstCode = detectedCodes[0]
      if (detectedCodes.length > 0 && firstCode && firstCode.rawValue) {
        onScan(firstCode.rawValue)
      }
    },
    [onScan]
  )

  const handleError = useCallback(
    (err: unknown) => {
      const error = err instanceof Error ? err : new Error(String(err))
      console.error('QR Scanner error:', error)

      // Check for permission denied
      if (
        error.message.includes('Permission') ||
        error.message.includes('NotAllowedError')
      ) {
        setHasPermission(false)
        setError('Camera access denied. Please allow camera access to scan QR codes.')
      } else if (
        error.message.includes('NotFoundError') ||
        error.message.includes('No camera')
      ) {
        setError('No camera found on this device.')
      } else {
        setError('Failed to access camera. Please try again.')
      }

      onError?.(error)
    },
    [onError]
  )

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm">
      <div className="absolute inset-0 scanlines opacity-50 pointer-events-none"></div>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors border border-white/10"
        aria-label="Close scanner"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div className="w-full max-w-md mx-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block px-3 py-1 bg-primary/10 border border-primary/30 rounded text-primary text-xs font-bold uppercase tracking-widest mb-3 animate-pulse">
            Scanner Active
          </div>
          <h2 className="text-2xl font-display font-bold text-white uppercase tracking-wider neon-glow">Target Acquisition</h2>
          <p className="text-sm text-white/50 mt-2 font-mono">
            // ALIGN QR CODE WITHIN RETICLE //
          </p>
        </div>

        {/* Scanner or Error */}
        {error ? (
          <div className="aspect-square rounded-2xl glass-panel border border-red-500/30 flex flex-col items-center justify-center p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-red-500/5 animate-pulse"></div>
            <div className="text-5xl mb-6 relative z-10">
              {!hasPermission ? '🔒' : '⚠️'}
            </div>
            <h3 className="text-red-400 font-bold uppercase tracking-wider mb-2 relative z-10">Scan Failure</h3>
            <p className="text-white/70 text-sm font-mono relative z-10 mb-6">{error}</p>
            <button
              onClick={onClose}
              className="px-8 py-3 rounded bg-red-500/20 text-red-400 border border-red-500/50 font-bold uppercase tracking-widest hover:bg-red-500/30 transition-all relative z-10"
            >
              Abort
            </button>
          </div>
        ) : (
          <div className="relative aspect-square rounded-2xl overflow-hidden bg-black border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)]">
            <Scanner
              onScan={handleScan}
              onError={handleError}
              formats={['qr_code']}
              components={{
                torch: true,
              }}
              styles={{
                container: {
                  width: '100%',
                  height: '100%',
                },
                video: {
                  objectFit: 'cover',
                },
              }}
            />
            {/* Scan frame overlay (HUD) */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)]"></div>

              {/* Reticle */}
              <div className="absolute inset-[15%] border border-primary/30 rounded-lg shadow-[0_0_20px_rgba(242,13,128,0.2)]">
                {/* Corner accents */}
                <div className="absolute -top-1 -left-1 w-8 h-8 border-t-2 border-l-2 border-primary rounded-tl-lg shadow-[0_0_10px_theme('colors.primary')]"></div>
                <div className="absolute -top-1 -right-1 w-8 h-8 border-t-2 border-r-2 border-primary rounded-tr-lg shadow-[0_0_10px_theme('colors.primary')]"></div>
                <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-2 border-l-2 border-primary rounded-bl-lg shadow-[0_0_10px_theme('colors.primary')]"></div>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-2 border-r-2 border-primary rounded-br-lg shadow-[0_0_10px_theme('colors.primary')]"></div>

                {/* Scanning line */}
                <div className="absolute top-0 left-0 w-full h-0.5 bg-primary/50 shadow-[0_0_10px_theme('colors.primary')] animate-[scan_2s_ease-in-out_infinite]"></div>

                {/* Crosshairs */}
                <div className="absolute top-1/2 left-1/2 w-4 h-[1px] bg-primary/50 -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute top-1/2 left-1/2 w-[1px] h-4 bg-primary/50 -translate-x-1/2 -translate-y-1/2"></div>
              </div>

              {/* Data readouts */}
              <div className="absolute top-4 left-4 text-[10px] text-primary/70 font-mono">
                <div>REC: ACTIVE</div>
                <div>ISO: AUTO</div>
              </div>
              <div className="absolute bottom-4 right-4 text-[10px] text-primary/70 font-mono text-right">
                <div>QR_DECODER_V1</div>
                <div>READY</div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel button */}
        <button
          onClick={onClose}
          className="w-full mt-6 py-4 rounded bg-white/5 text-white/60 font-medium border border-white/10 hover:bg-white/10 hover:text-white transition-colors uppercase tracking-widest text-xs"
        >
          Close Scanner
        </button>
      </div>
    </div>
  )
}
